import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { componentTypes } from '@white-nl/instantsearch-components';
import SessionContext from '../../../contexts/SessionContext';
import Picture from '../../ui/Picture';
import { ChevronLeft, ChevronRight, ImageSlash } from '../../ui/icons/sharp/regular';
import { Heart as HeartRegular } from '../../ui/icons/classic/regular';
import { Heart as HeartSolid } from '../../ui/icons/classic/solid';
import { initColorSlider } from '../../../../../components/Catalog';
import StockIndicator from '../../ui/icons/StockIndicator';
import { useAddToWishlistMutation, useRemoveFromWishlistMutation } from '../../../redux-modules/service';

interface Image {
  title: string;
  transforms: Record<string, { url: string; width: string; height: string }>;
}

interface OtherColorImages {
  colorPreview: Image;
  colorFull: Image;
}

interface OtherColor {
  title: string;
  uri: string;
  active: boolean;
  productImages: OtherColorImages;
}

export default function HitWithWishlist({ hit, results, additionalData }: componentTypes.HitProps) {
  const {
    id,
    productBrand,
    image,
    badges,
    isOnPromotion,
    discountPercentage,
    priceFormatted,
    salePriceFormatted,
    otherColors,
    productModel,
    hitCategory,
  } = hit;

  const {
    sessionInfo,
    hitClasses,
    backgroundColor,
    translates,
    salesCard,
    usps,
    storeCard,
    wishlistIds,
  } = additionalData;

  const [addToWishlist] = useAddToWishlistMutation();
  const [removeFromWishlist] = useRemoveFromWishlistMutation();

  // Determine if the item is in the wishlist
  const isInWishlist = useMemo(() => (wishlistIds || []).includes(hit.id), [wishlistIds, hit.id]);

  // State for wishlist active status
  const [wishlistActive, setWishlistActive] = useState(isInWishlist);

  useEffect(() => {
    setWishlistActive(isInWishlist);
  }, [isInWishlist]);

  const onWishlistClick = useCallback((e:
                                         React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (sessionInfo && !sessionInfo.isGuest) {
      if (wishlistActive) {
        removeFromWishlist({ elementId: hit.id, sessionInfo });
        setWishlistActive(false);
      } else {
        addToWishlist({ elementId: hit.id, sessionInfo });
        setWishlistActive(true);
      }
    } else {
      window.location = Craft.getSiteUrl('inloggen', { redirect: window.location.href });
    }
  }, [
    hit.id,
    sessionInfo,
    wishlistActive,
    addToWishlist,
    removeFromWishlist,
  ]);

  const asCustomCurrency = useCallback((value: string) => {
    let formattedValue = value.replace('.', ',');
    if (formattedValue.endsWith(',00')) {
      formattedValue = formattedValue.replace(',00', ',-');
    }
    return formattedValue;
  }, []);

  const hitIndex = results.hits.findIndex((h) => h.id === id);
  const lazyLoadImage = hitIndex > 3;
  const price = asCustomCurrency(isOnPromotion === 1 ? salePriceFormatted : priceFormatted);
  const productBrandTitle = Array.isArray(productBrand)
    ? productBrand[0]?.title
    : productBrand?.title;
  const brandTitle = productBrandTitle ?? hit.title ?? '';

  const [hoveredColorId, setHoveredColorId] = useState<string>(hit.uri);

  const handlePreviewMouseEnter = (uri: string) => {
    setHoveredColorId(uri);
  };

  const handlePreviewMouseLeave = () => {
    setHoveredColorId(hit.uri);
  };

  const handlePreviewClick = (event: React.MouseEvent<HTMLButtonElement>, uri: string) => {
    event.preventDefault();
    event.stopPropagation();
    window.location = Craft.getUrl(uri);
  };

  useEffect(() => {
    initColorSlider(`product-color-slider-${hit.id}`);
  }, [hit.id]);

  return (
    <SessionContext.Provider value={sessionInfo || false}>
      <div className={hitClasses ?? 'w-full flex aspect-square 3xs:aspect-10/13 overflow-hidden'}>
        <a
          href={Craft.getSiteUrl(hit.uri, '')}
          className={`group/card ${backgroundColor ?? 'bg-gray-100'} w-full block relative h-full p-3 pt-9.25 lg:p-6 lg:pt-11.25 flex flex-col no-underline transition-all`}
        >
          <div
            className="absolute top-0 p-3 lg:p-6 lg:pb-4 pb-0 left-0 w-full flex flex-wrap gap-1 items-start xs:items-center justify-between z-3"
          >
            {badges
              && (
                <div className="flex flex-wrap flex-1 items-center gap-1 xs:gap-1.5">
                  {badges.new
                    && (
                      <span
                        className="bg-brand shrink-0 text-white text-xs xs:text-base px-1.5 py-0.75 xs:py-1 rounded-xs uppercase"
                      >
                        {Craft.t(translates.catalog, 'New')}
                      </span>
                    )}
                  {discountPercentage
                    && (
                      <span
                        className="bg-accent shrink-0 text-white text-xs xs:text-base px-1.5 py-0.75 xs:py-1 rounded-xs"
                      >
                        {`-${discountPercentage}`}
                      </span>
                    )}
                  {badges.lastItems
                    && (
                      <span
                        className="shrink-0 justify-start items-center gap-x-1.5 flex text-xs xs:text-base px-1.5 py-0.75 xs:py-1 text-warning"
                      >
                        <StockIndicator iconClass="w-4 h-4 mt-px" />
                        <span className="text-dark max-xs:w-10 leading-2.5">
                          {Craft.t(translates.catalog, 'Last Stock')}
                        </span>
                      </span>
                    )}
                </div>
              )}
            <button
              className={`${wishlistActive ? 'active ' : ''} group/btn-favorite transition relative flex items-center justify-center cursor-pointer z-10 shrink-0 ms-auto pointer-events-auto w-4.75 h-4.75 p-0.25 lg:w-5.25 lg:h-5.25 lg:p-0`}
              type="button"
              onClick={onWishlistClick}
            >
              <span className="sr-only">
                {wishlistActive
                  ? Craft.t('product', 'remove from wishlist')
                  : Craft.t('product', 'add to wishlist')}
              </span>
              <HeartRegular
                iconClass="w-full fill-accent"
              />
              <HeartSolid
                iconClass="w-full fill-accent absolute opacity-0 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 group-[.active]/btn-favorite:opacity-100 group-hover/btn-favorite:opacity-100 transition-all"
              />
            </button>
          </div>
          {otherColors === undefined && hoveredColorId === hit.uri && (
            <div
              className="absolute px-3 lg:px-6 top-0 3xs:top-8 xs:top-10 left-0 aspect-square w-full overflow-hidden z-2"
            >
              {image ? (
                <Picture
                  pictureClasses={`block aspect-square flex items-center ${backgroundColor ?? 'bg-gray-100'} relative w-full overflow-hidden`}
                  image={image}
                  classes="w-full mix-blend-darken object-scale-down object-center scale-80 transition-all duration-400 group-hover/card:scale-100"
                  defaultTransform="all"
                  loading={lazyLoadImage ? 'lazy' : 'eager'}
                />
              ) : (
                <ImageSlash
                  width={310}
                  height={310}
                  iconClass="text-light-grey-2 w-32 absolute top-1/2 left-1/2 -translate-1/2"
                />
              )}
            </div>
          )}
          {otherColors && otherColors.length > 0
            && otherColors.map((otherColor: OtherColor) => {
              if (hoveredColorId !== otherColor.uri) return null;
              return (
                <div
                  key={otherColor.uri}
                  className="absolute px-3 lg:px-6 top-0 3xs:top-8 xs:top-10 left-0 aspect-square w-full overflow-hidden z-1"
                >
                  {otherColor.productImages.colorFull ? (
                    <Picture
                      pictureClasses={`block aspect-square flex ${backgroundColor ?? 'bg-gray-100'} items-center relative w-full overflow-hidden`}
                      image={otherColor.productImages.colorFull}
                      classes="w-full h-auto mix-blend-darken object-scale-down object-center scale-80 transition-all duration-400 group-hover/card:scale-100"
                      defaultTransform="all"
                      loading={lazyLoadImage ? 'lazy' : 'eager'}
                    />
                  ) : (
                    <ImageSlash
                      width={310}
                      height={310}
                      iconClass="text-light-grey-2 w-32 absolute top-1/2 left-1/2 -translate-1/2"
                    />
                  )}
                </div>
              );
            })}
          <div
            className={`flex flex-col items-center justify-center gap-1 lg:gap-2 absolute left-0 bottom-3 lg:bottom-6 w-full z-5 transition-all duration-400 ease-in-out ${otherColors ? 'group-hover/card:scale-89 group-hover/card:bottom-11 lg:group-hover/card:bottom-14' : ''}`}
          >
            <div
              className="text-base/4 lg:text-lg/6 font-bold text-dark text-center no-underline line-clamp-2"
            >
              {brandTitle}
            </div>
            <div
              className="text-base/4 lg:text-md/6 text-dark text-center no-underline line-clamp-2"
            >
              {productModel}
              {' '}
              -
              {' '}
              {hitCategory}
            </div>
            <div
              className={`flex items-center gap-2 ${otherColors ? 'group-hover/card:opacity-0' : ''}`}
            >
              <span
                className={`text-base/4 lg:text-lg/6 ${isOnPromotion === 1 ? 'text-accent' : 'text-dark'} no-underline`}
              >
                {price}
              </span>
              {isOnPromotion === 1
                && (
                  <span className="text-base/4 text-gray-300 font-thin line-through">
                    {asCustomCurrency(priceFormatted)}
                  </span>
                )}
            </div>
          </div>
          {otherColors && (
            <div
              className={`${backgroundColor ?? 'bg-gray-100'} absolute left-0 bottom-3 lg:bottom-6 w-full opacity-0 h-16 group-hover/card:opacity-100 group-hover/card:z-4 duration-500 z-0 transition-all`}
            >
              <div
                id={`product-color-slider-${hit.id}`}
                className="relative w-full px-9.25"
              >
                <div className="product-color-swiper swiper">
                  <div className="swiper-wrapper">
                    {otherColors && otherColors.length > 0
                      && otherColors.map((otherColor: OtherColor) => (
                        <div
                          key={`slide-${otherColor.uri}`}
                          className="swiper-slide size-16!"
                        >
                          <button
                            type="button"
                            title={otherColor.title}
                            className={`cursor-pointer size-full ${backgroundColor ?? 'bg-gray-100'}`}
                            onMouseEnter={() => handlePreviewMouseEnter(otherColor.uri)}
                            onMouseLeave={handlePreviewMouseLeave}
                            onClick={(e) => handlePreviewClick(e, otherColor.uri)}
                          >
                            {otherColor.productImages.colorPreview ? (
                              <Picture
                                pictureClasses="block relative mix-blend-darken aspect-square flex w-full overflow-hidden"
                                image={otherColor.productImages.colorPreview}
                                classes="object-scale-down object-center w-full h-full mix-blend-darken"
                                defaultTransform="all"
                                loading={lazyLoadImage ? 'lazy' : 'eager'}
                              />
                            ) : (
                              <ImageSlash
                                width={64}
                                height={64}
                                iconClass="text-light-grey-2 w-12 absolute top-1/2 left-1/2 -translate-1/2"
                              />
                            )}
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
                <button
                  type="button"
                  className="group/btn cursor-pointer h-16 product-card-color-swiper-button-prev absolute left-2.25 top-0 z-2 size-6 p-2"
                >
                  <ChevronLeft
                    iconClass="w-2 h-auto fill-dark group-hover/btn:fill-accent transition-all translate-x-0.25 relative z-2"
                  />
                </button>
                <button
                  type="button"
                  className="group/btn cursor-pointer product-card-color-swiper-button-next h-16 absolute right-2.25 top-0 size-6 z-2 p-2"
                >
                  <ChevronRight
                    iconClass="w-2 h-auto fill-dark group-hover/btn:fill-accent transition-all translate-x-0.25 relative z-2"
                  />
                </button>
              </div>
            </div>
          )}
        </a>
      </div>
      {
        hitIndex === 6
        && salesCard
        && (
          <div
            className="w-full aspect-320/418 flex"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: salesCard }}
          />
        )
      }
      {
        hitIndex === 6
        && usps
        && (
          <div
            className="w-full w-full py-7.5 col-span-full"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: usps }}
          />
        )
      }
      {
        hitIndex === 10
        && storeCard
        && (
          <div
            className="w-full aspect-320/418 flex"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: storeCard }}
          />
        )
      }
    </SessionContext.Provider>
  );
}
